// Os campos do par PEM, compartilhados por cadastrar e substituir.
//
// Duas formas de informar: enviar os arquivos (.crt/.pem + .key) ou colar o PEM.
// O arquivo é o caminho comum — é nesse formato que o banco entrega —, e colar
// existe para quem recebeu o conteúdo por outro meio.
//
// O arquivo alimenta o MESMO campo do formulário que a aba de colar, então a
// validação de header e o submit são um só caminho: a aba escolhida muda apenas
// como o texto entra.
import { UploadCloudIcon } from "lucide-react"
import { useState } from "react"
import type {
  FieldValues,
  Path,
  UseFormReturn,
} from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  FileInput,
  FileUploader,
} from "@/components/ui/file-upload"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Label } from "@/components/ui/label"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"

const panelPrefix = "pages.adminMerchants"
const certificatePrefix =
  "pages.adminProcessorConfig.certificate"

/** Lê o arquivo como TEXTO (PEM) — a conversão para base64 é no submit. */
const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () =>
      resolve(String(reader.result ?? ""))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })

type PemValues = FieldValues & {
  certificatePem: string
  privateKeyPem: string
}

type MtlsCertificatePemFieldsProps<
  T extends PemValues = PemValues,
> = {
  form: UseFormReturn<T>
}

export const MtlsCertificatePemFields = <
  T extends PemValues,
>({
  form,
}: MtlsCertificatePemFieldsProps<T>) => {
  const { t } = useTranslation()
  const [certFiles, setCertFiles] = useState<File[] | null>(
    null
  )
  const [keyFiles, setKeyFiles] = useState<File[] | null>(
    null
  )

  const handleFiles =
    (field: "certificatePem" | "privateKeyPem") =>
    async (files: File[] | null) => {
      const text = files?.[0]
        ? await readFileAsText(files[0])
        : ""
      if (field === "certificatePem") {
        setCertFiles(files)
      } else {
        setKeyFiles(files)
      }
      form.setValue(field as Path<T>, text as T[Path<T>], {
        shouldValidate: Boolean(text),
      })
    }

  const errors = form.formState.errors

  return (
    <Tabs defaultValue="upload">
      <TabsList>
        {/* Arquivo primeiro: é o formato em que o banco entrega. */}
        <TabsTrigger value="upload">
          {t(`${certificatePrefix}.uploadTab`)}
        </TabsTrigger>
        <TabsTrigger value="paste">
          {t(`${certificatePrefix}.pasteTab`)}
        </TabsTrigger>
      </TabsList>

      <TabsContent
        value="upload"
        className="space-y-4 pt-2"
      >
        <div className="space-y-1.5">
          <Label>
            {t(`${certificatePrefix}.certificateFileLabel`)}
          </Label>
          <FileUploader
            value={certFiles}
            onValueChange={handleFiles("certificatePem")}
            dropzoneOptions={{
              accept: {
                "application/x-x509-ca-cert": [
                  ".crt",
                  ".pem",
                  ".cer",
                ],
              },
              maxFiles: 1,
              multiple: false,
            }}
          >
            <FileInput className="rounded-lg border border-dashed">
              <div className="flex flex-col items-center gap-1 py-4 text-center">
                <UploadCloudIcon
                  className="size-5 text-muted-foreground"
                  aria-hidden="true"
                />
                <span className="text-xs text-muted-foreground">
                  {certFiles?.[0]?.name ??
                    t(
                      `${certificatePrefix}.certificateFileHint`
                    )}
                </span>
              </div>
            </FileInput>
          </FileUploader>
        </div>

        <div className="space-y-1.5">
          <Label>
            {/* Chave própria: no painel de processors a chave é opcional
                (ausente = mantém a atual) e a copy de lá diz isso. No cofre
                não existe "manter" — sem o par não há mTLS. */}
            {t(
              `${panelPrefix}.mtlsCertificates.privateKeyFileLabel`
            )}
          </Label>
          <FileUploader
            value={keyFiles}
            onValueChange={handleFiles("privateKeyPem")}
            dropzoneOptions={{
              accept: {
                "application/x-pem-file": [".key", ".pem"],
              },
              maxFiles: 1,
              multiple: false,
            }}
          >
            <FileInput className="rounded-lg border border-dashed">
              <div className="flex flex-col items-center gap-1 py-4 text-center">
                <UploadCloudIcon
                  className="size-5 text-muted-foreground"
                  aria-hidden="true"
                />
                <span className="text-xs text-muted-foreground">
                  {keyFiles?.[0]?.name ??
                    t(
                      `${certificatePrefix}.privateKeyFileHint`
                    )}
                </span>
              </div>
            </FileInput>
          </FileUploader>
        </div>

        {/* O erro do arquivo aparece nesta aba: o campo é o mesmo, mas o
            Textarea está na outra e a mensagem ficaria escondida. */}
        {errors.certificatePem && (
          <p className="text-sm font-medium text-destructive">
            {String(errors.certificatePem.message)}
          </p>
        )}
        {errors.privateKeyPem && (
          <p className="text-sm font-medium text-destructive">
            {String(errors.privateKeyPem.message)}
          </p>
        )}
      </TabsContent>

      <TabsContent value="paste" className="space-y-4 pt-2">
        <FormField
          control={form.control}
          name={"certificatePem" as Path<T>}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(
                  `${panelPrefix}.mtlsCertificates.certificatePem`
                )}
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  rows={5}
                  className="font-mono text-xs"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={"privateKeyPem" as Path<T>}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(
                  `${panelPrefix}.mtlsCertificates.privateKeyPem`
                )}
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  rows={5}
                  className="font-mono text-xs"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </TabsContent>
    </Tabs>
  )
}
